import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Label,
  type AccordionToggleEventHandler,
  tokens,
} from '@fluentui/react-components';
import {
  Add16Filled,
  ChevronRight20Regular,
  Delete16Filled,
} from '@fluentui/react-icons';
import { parseOtherInfo, type IField } from '@moko/utils';
import { useOtherInfoContext } from './OtherInfoContext';
import { FieldList } from './FieldList';

interface MobileRepeatingPanelProps {
  field: IField;
  fields: IField[];
}

export const MobileRepeatingPanel = ({
  field,
  fields,
}: MobileRepeatingPanelProps) => {
  const {
    params,
    otherInfo,
    getRepeatingPanelIndex,
    setRepeatingPanelIndex,
    handleRepeatingPanelAction,
  } = useOtherInfoContext();
  const readOnly = Boolean(params.readOnly);

  const entries = useMemo(() => {
    const parsed = parseOtherInfo(otherInfo);
    return Array.isArray(parsed[field.name]) ? parsed[field.name] : [];
  }, [otherInfo, field.name]);

  const currentIndex = getRepeatingPanelIndex(field.name);
  const [collapsedByUser, setCollapsedByUser] = useState(false);
  const prevIndexRef = useRef(currentIndex);

  useEffect(() => {
    if (currentIndex !== prevIndexRef.current) {
      setCollapsedByUser(false);
      prevIndexRef.current = currentIndex;
    }
  }, [currentIndex]);

  const openItems = collapsedByUser
    ? []
    : entries.length > 0
    ? [String(currentIndex)]
    : [];

  const handleToggle: AccordionToggleEventHandler = useCallback(
    (_event, data) => {
      const items = data.openItems as string[];
      if (items.length > 0) {
        const newIndex = Number(items[0]);
        setCollapsedByUser(false);
        if (newIndex !== currentIndex) {
          setRepeatingPanelIndex(field.name, newIndex);
        }
      } else {
        setCollapsedByUser(true);
      }
    },
    [setRepeatingPanelIndex, field.name, currentIndex]
  );

  const handleAdd = useCallback(() => {
    handleRepeatingPanelAction(field.name, 'add');
    setCollapsedByUser(false);
  }, [handleRepeatingPanelAction, field.name]);

  const handleRemove = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      setRepeatingPanelIndex(field.name, index);
      // Use setTimeout to ensure the index is set before remove runs
      setTimeout(() => {
        handleRepeatingPanelAction(field.name, 'remove');
      }, 0);
    },
    [setRepeatingPanelIndex, handleRepeatingPanelAction, field.name]
  );

  return (
    <div
      id={'field_' + field.name}
      style={{
        width: '100%',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '5px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '8px 10px',
          backgroundColor: tokens.colorBrandBackgroundInvertedSelected,
        }}
      >
        <Label style={{ fontWeight: 600, fontSize: '15px' }}>
          {field.label}
        </Label>
      </div>
      <Accordion collapsible openItems={openItems} onToggle={handleToggle}>
        {entries.map((_entry: unknown, index: number) => (
          <AccordionItem key={index} value={String(index)}>
            {index > 0 && (
              <div
                style={{ borderTop: `1px solid ${tokens.colorNeutralStroke1}` }}
              />
            )}
            <AccordionHeader
              expandIconPosition="end"
              style={{
                backgroundColor: tokens.colorBrandBackgroundInvertedSelected,
              }}
              expandIcon={
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!readOnly && entries.length > 1 && (
                    <Delete16Filled
                      style={{
                        cursor: 'pointer',
                        color: tokens.colorPaletteRedForeground1,
                      }}
                      onClick={(e) =>
                        handleRemove(e as React.MouseEvent, index)
                      }
                    />
                  )}
                  <ChevronRight20Regular
                    style={{
                      transition: 'transform 0.2s',
                      transform: openItems.includes(String(index))
                        ? 'rotate(90deg)'
                        : 'rotate(0deg)',
                    }}
                  />
                </div>
              }
            >
              {field.label} #{index + 1}
            </AccordionHeader>
            <AccordionPanel>
              {currentIndex === index && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '14px',
                    padding: '10px 0',
                  }}
                >
                  <FieldList fields={fields} parentName={field.name} />
                </div>
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      {!readOnly && (
        <div style={{ padding: '8px' }}>
          <Button
            appearance="primary"
            icon={<Add16Filled />}
            onClick={handleAdd}
            style={{ width: '100%' }}
          >
            Add Another {field.label}
          </Button>
        </div>
      )}
    </div>
  );
};
